import { exhaustSwitchCase } from '@csp/csp-common-util';
import {
  ConsentFeatureTypeV1,
  QuestionnaireTraining,
  ScheduledConsentActionStatusTypeV1,
  ScheduledConsentActionStatusV1,
  ScheduledConsentTypeV1,
  StepByStepTraining,
} from '@csp/dto';
import { CspError } from '@csp/csp-common-model';
import { ConsentActionV1 } from '@csp/dmdp-api-consent-dto';
import { ConsentFeatureType } from '../model/ConsentConfigOnFeatureV1';
import { ScheduledConsent } from '../model/ScheduledConsent';
import { parseStringifiedDate } from '../mapper/ConsentResponseMapper';
import { ConsentDataWithdrawOptions } from '../mapper/defaultMetaProperties';
import { SkippedConsent } from '../model/AnsweredConsent';
import { ConsentActionStatusValue } from '../model/ConsentAction';

export const transformConsentFeatureType = (onFeature: ConsentFeatureTypeV1): ConsentFeatureType => {
  switch (onFeature) {
    case ConsentFeatureTypeV1.ANALYTICS:
      return ConsentFeatureType.ANALYTICS;
    case ConsentFeatureTypeV1.ON_ONBOARDING:
      return ConsentFeatureType.ON_ONBOARDING;
    case ConsentFeatureTypeV1.TELEHEALTH:
      return ConsentFeatureType.TELEHEALTH;
    default:
      return exhaustSwitchCase(onFeature);
  }
};

export const consentShouldBePrompted = <
  T extends ScheduledConsent | ScheduledConsentTypeV1 | QuestionnaireTraining | StepByStepTraining,
>(
  scheduledConsent: T,
): boolean => scheduledConsent.artifactStatus.shouldPrompt;

export const calculateConsentActionStatus = (
  skippedConsent: SkippedConsent | undefined,
  consentAction: ConsentActionV1 | undefined,
): ScheduledConsentActionStatusV1 => {
  if (consentAction) {
    const status = consentActionV1ToScheduledConsentActionStatusTypeV1(consentAction);
    const timestamp = consentAction.status?.localTimestamp
      ? consentAction.status?.localTimestamp
      : consentAction.status?.createdAt;
    const statusUpdateTimestamp = timestamp ? parseStringifiedDate(timestamp).toISOString() : '';

    return {
      status,
      statusUpdateTimestamp,
    };
  } else if (skippedConsent) {
    return {
      status: ScheduledConsentActionStatusTypeV1.REJECTED,
      statusUpdateTimestamp: skippedConsent.skippedAt,
    };
  } else {
    return {
      status: ScheduledConsentActionStatusTypeV1.PENDING,
    };
  }
};

export const consentActionV1ToScheduledConsentActionStatusTypeV1 = (
  consentAction: ConsentActionV1,
): Exclude<ScheduledConsentActionStatusTypeV1, ScheduledConsentActionStatusTypeV1.PENDING> => {
  const options: ConsentDataWithdrawOptions | undefined = consentAction.status?.options;

  // TODO OFRAZU-48272 Remove ? in consentAction.status when api spec type has been corrected
  if (consentAction.status?.value === ConsentActionStatusValue.WITHDRAWN) {
    if (options?.SoftDeleteHistory === 'true') {
      return ScheduledConsentActionStatusTypeV1.WITHDRAWN_STOP_USING_COLLECTED_DATA;
    } else if (options?.SoftDeleteHistory === 'false') {
      return ScheduledConsentActionStatusTypeV1.WITHDRAWN_CONTINUE_USING_COLLECTED_DATA;
    } else {
      return ScheduledConsentActionStatusTypeV1.WITHDRAWN;
    }
  } else if (consentAction.status?.value === ConsentActionStatusValue.APPROVED) {
    return ScheduledConsentActionStatusTypeV1.APPROVED;
  } else if (consentAction.status?.value === ConsentActionStatusValue.REJECTED) {
    return ScheduledConsentActionStatusTypeV1.REJECTED;
  } else {
    // TODO OFRAZU-48272 Should we throw error or just return null or similar? status.value is typed as optional but is probably wrong
    throw CspError.badState('Could not calculate consent status');
  }
};
